import React, { useState, useMemo, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { Toast } from './components/Toast';
import { Icons } from './components/Icons';
import { Dashboard } from './components/views/Dashboard';
import { PatientRegistry } from './components/views/PatientRegistry';
import { AppointmentsView } from './components/views/Appointments';
import { CalendarView } from './components/views/CalendarView';
import { FollowUpView } from './components/views/FollowUp';
import { ReminderQueue } from './components/views/ReminderQueue';
import { Settings } from './components/views/Settings';
import { ReportsView } from './components/views/Reports';
import { PaymentsView } from './components/views/Payments';
import { ExpensesView } from './components/views/Expenses';
import PanoramicAnalysis from './components/views/PanoramicAnalysis';
import { PatientModal } from './components/modals/PatientModal';
import { AppointmentModal } from './components/modals/AppointmentModal';
import { INITIAL_PATIENTS, INITIAL_DENTISTS, INITIAL_PROCEDURES, INITIAL_CLINIC_SETTINGS, INITIAL_APPOINTMENTS } from './constants/initialData';
import { PANORAMIC_ANALYSIS } from './constants/panoramicAnalysis';
import { Patient, Appointment, TabId, ToastState, ClinicSettings, Dentist, Procedure, ReminderItem, ReminderStatus, PaymentRecord, ExpenseRecord } from './types';
import { checkAppointmentConflict, findAvailableSlots } from './utils/appointmentUtils';

const App: React.FC = () => {
  type UserSession =
    | { role: 'admin' }
    | { role: 'secretary' }
    | { role: 'doctor'; doctorId: string }
    | { role: 'patient'; patientId: string };

  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [patients, setPatients] = useState<Patient[]>(INITIAL_PATIENTS);
  const [appointments, setAppointments] = useState<Appointment[]>(INITIAL_APPOINTMENTS);
  const [dentists, setDentists] = useState<Dentist[]>(INITIAL_DENTISTS);
  const [procedures, setProcedures] = useState<Procedure[]>(INITIAL_PROCEDURES);
  const [clinicSettings, setClinicSettings] = useState<ClinicSettings>(INITIAL_CLINIC_SETTINGS);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(INITIAL_PATIENTS[0]?.id ?? null);
  const rooms = clinicSettings.treatmentRooms?.length ? clinicSettings.treatmentRooms : ['Chair 1'];
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);
  const [loginRole, setLoginRole] = useState<'admin' | 'secretary' | 'doctor' | 'patient'>('admin');
  const [loginDoctorId, setLoginDoctorId] = useState<string>(dentists[0]?.id ?? '');
  const [loginPatientId, setLoginPatientId] = useState<string>(patients[0]?.id ?? '');
  const [loginPin, setLoginPin] = useState<string>('');
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [paymentIntent, setPaymentIntent] = useState<{ patientId?: string; appointmentId?: string; amount?: number } | null>(null);

  const roleOptions: { value: 'admin' | 'secretary' | 'doctor' | 'patient'; label: string; desc: string; icon: keyof typeof Icons }[] = [
    { value: 'admin', label: 'Admin', desc: 'Full control & reports', icon: 'ShieldCheck' },
    { value: 'secretary', label: 'Front Desk', desc: 'Scheduling & reminders', icon: 'Users' },
    { value: 'doctor', label: 'Doctor', desc: 'Chair-side view', icon: 'Stethoscope' },
    { value: 'patient', label: 'Patient', desc: 'Self-service portal', icon: 'UserCircle' },
  ];
  useEffect(() => {
    if (loginRole === 'doctor') {
      const exists = dentists.find(d => d.id === loginDoctorId);
      if (!exists && dentists.length > 0) {
        setLoginDoctorId(dentists[0].id);
      }
    } else if (loginRole === 'patient') {
      const exists = patients.find(p => p.id === loginPatientId);
      if (!exists && patients.length > 0) {
        setLoginPatientId(patients[0].id);
      }
    }
  }, [loginRole, loginDoctorId, loginPatientId, dentists, patients]);

  useEffect(() => {
    if (currentUser?.role === 'doctor') {
      const exists = dentists.find(d => d.id === currentUser.doctorId);
      if (!exists) {
        setCurrentUser(null);
      }
    } else if (currentUser?.role === 'patient') {
      const exists = patients.find(p => p.id === currentUser.patientId);
      if (!exists) {
        setCurrentUser(null);
      }
    }
  }, [currentUser, dentists, patients]);
  const currentUserProfile = useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === 'doctor') {
      const doc = dentists.find(d => d.id === currentUser.doctorId);
      return {
        name: doc?.name || 'Doctor',
        title: doc?.specialty || 'Doctor',
        email: `${(doc?.name || 'doctor').toLowerCase().replace(/\s+/g, '.')}@dentalcore.com`,
      };
    }
    if (currentUser.role === 'patient') {
      const patient = patients.find(p => p.id === currentUser.patientId);
      return {
        name: patient?.name || 'Patient',
        title: 'Patient',
        email: patient?.email || `${(patient?.name || 'patient').toLowerCase().replace(/\s+/g, '.')}@email.com`,
      };
    }
    if (currentUser.role === 'secretary') {
      return { name: 'Front Desk', title: 'Secretary', email: 'frontdesk@dentalcore.com' };
    }
    return { name: 'Clinic Admin', title: 'Administrator', email: 'admin@dentalcore.com' };
  }, [currentUser, dentists, patients]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showToast, setShowToast] = useState<ToastState>({ show: false, message: '', type: 'success' });

  // Modals
  const [isApptModalOpen, setIsApptModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState<Appointment | null>(null);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  // Helper to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Forms
  const getEmptyAppt = (): Omit<Appointment, 'id' | 'startTime'> => ({
    patientId: '',
    providerId: dentists[0]?.id || 'd1',
    date: getTodayDate(),
    time: '09:00',
    duration: 30,
    type: 'Cleaning',
    room: rooms[0],
    status: 'SCHEDULED',
    internalNotes: '',
    patientNotes: '',
    isWalkIn: false,
  });

  const getEmptyPatient = (): Omit<Patient, 'id'> => ({
    name: '',
    phone: '',
    email: '',
    dob: '',
    address: '',
    gender: 'Female',
    insurance: '',
    emergencyContact: { name: '', phone: '', relationship: '' },
    alerts: [],
    dentalHistory: '',
    attachments: [],
    auditLog: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const [apptForm, setApptForm] = useState<Omit<Appointment, 'id' | 'startTime'>>(getEmptyAppt());
  const [patientForm, setPatientForm] = useState<Omit<Patient, 'id'>>(getEmptyPatient());
  const [calendarFocusDate, setCalendarFocusDate] = useState<string | undefined>(() => getTodayDate());

  // Keep a patient selected when data changes
  useEffect(() => {
    if (patients.length === 0) {
      setSelectedPatientId(null);
      return;
    }

    const stillExists = selectedPatientId && patients.some(p => p.id === selectedPatientId);
    if (!stillExists) {
      setSelectedPatientId(patients[0].id);
    }
  }, [patients, selectedPatientId]);

  // Toast trigger
  const triggerToast = (message: string, type: 'success' | 'danger' = 'success') => {
    setShowToast({ show: true, message, type });
    setTimeout(() => setShowToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // SAVE APPOINTMENT
  const handleSaveAppt = (e: React.FormEvent) => {
    e.preventDefault();

    if (!apptForm.patientId) {
      alert('Please select a patient');
      return;
    }

    // Check for conflicts before saving
    const conflict = checkAppointmentConflict(apptForm, appointments, editingAppt?.id);
    if (conflict.hasConflict) {
      triggerToast(`Cannot book: ${conflict.message}`, 'danger');
      return;
    }

    const startStr = `${apptForm.date}T${apptForm.time}:00`;

    if (editingAppt) {
      // UPDATE existing appointment
      setAppointments(
        appointments.map(a =>
          a.id === editingAppt.id ? { ...apptForm, id: a.id, startTime: startStr } : a
        )
      );
      triggerToast('Appointment updated!');
      setCalendarFocusDate(apptForm.date);
    } else {
      // CREATE new appointment
      const newAppt: Appointment = {
        ...apptForm,
        id: `a${Date.now()}`,
        startTime: startStr,
      };
      setAppointments([...appointments, newAppt]);
      triggerToast('Appointment created!');
      setCalendarFocusDate(apptForm.date);
    }

    setIsApptModalOpen(false);
    setEditingAppt(null);
    setApptForm(getEmptyAppt());
  };

  // SAVE PATIENT
  const handleSavePatient = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingPatient) {
      // UPDATE existing patient
      setPatients(patients.map(p => (p.id === editingPatient.id ? { ...patientForm, id: p.id } : p)));
      triggerToast('Patient updated!');
    } else {
      // CREATE new patient
      const newPatient: Patient = {
        ...patientForm,
        id: `p${Date.now()}`,
      };
      setPatients([...patients, newPatient]);
      setSelectedPatientId(newPatient.id);
      triggerToast('Patient created!');
    }

    setIsPatientModalOpen(false);
    setEditingPatient(null);
    setPatientForm(getEmptyPatient());
  };

  const visibleAppointments = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'doctor') {
      return appointments.filter(a => a.providerId === currentUser.doctorId);
    }
    if (currentUser.role === 'patient') {
      return appointments.filter(a => a.patientId === currentUser.patientId);
    }
    // admin and secretary see all
    return appointments;
  }, [appointments, currentUser]);

  const visiblePatients = useMemo(() => {
    if (currentUser?.role === 'doctor') {
      const allowedIds = new Set(visibleAppointments.map(a => a.patientId));
      return patients.filter(p => allowedIds.has(p.id));
    }
    if (currentUser?.role === 'patient') {
      return patients.filter(p => p.id === currentUser.patientId);
    }
    return patients;
  }, [patients, currentUser, visibleAppointments]);

  const todayDateStr = new Date().toISOString().split('T')[0];

  const filteredPatients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return visiblePatients;

    return visiblePatients.filter(p => {
      const haystack = `${p.name} ${p.email} ${p.phone}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [visiblePatients, searchQuery]);

  // Keep selection within filtered set for faster “search to profile” navigation
  useEffect(() => {
    if (filteredPatients.length === 0) return;
    if (selectedPatientId && filteredPatients.some(p => p.id === selectedPatientId)) return;
    setSelectedPatientId(filteredPatients[0].id);
  }, [filteredPatients, selectedPatientId]);

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    setPatientForm(patient);
    setIsPatientModalOpen(true);
    setSelectedPatientId(patient.id);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    const updatedAppt =
      currentUser?.role === 'patient'
        ? { ...appointment, status: 'REQUESTED' as const }
        : appointment;
    setEditingAppt(appointment);
    setApptForm(updatedAppt);
    setIsApptModalOpen(true);
  };

  const handleDeleteAppointment = (id: string) => {
    setAppointments(prev => prev.filter(a => a.id !== id));
    triggerToast('Appointment deleted');
  };

  const handleCollectPaymentFromAppt = (appt: Appointment) => {
    setPaymentIntent({
      patientId: appt.patientId,
      appointmentId: appt.id,
      amount: clinicSettings.defaultPaymentAmount || 0,
    });
    setActiveTab('payments');
  };

  const enqueueReminder = (patientId: string, message?: string) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newReminder: ReminderItem = {
      id: `r${Date.now()}`,
      patientId,
      patientName: patient.name,
      contact: patient.email || patient.phone || 'N/A',
      status: 'queued',
      createdAt: new Date().toISOString(),
      message,
    };
    setReminders(prev => [newReminder, ...prev]);
    triggerToast('Reminder queued');
  };

  const updateReminderStatus = (id: string, status: ReminderStatus) => {
    setReminders(prev => prev.map(r => (r.id === id ? { ...r, status } : r)));
  };

  const deleteReminder = (id: string) => {
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const computeIsPaid = (appointmentId: string, nextPayments: PaymentRecord[]) => {
    const apptPayments = nextPayments.filter(p => p.appointmentId === appointmentId);
    if (apptPayments.length === 0) return false;
    const totalPaid = apptPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalDue = Math.max(
      clinicSettings.defaultPaymentAmount || 0,
      ...apptPayments.map(p => p.totalDue ?? p.amount)
    );
    return totalPaid >= totalDue;
  };

  const adjustAppointmentStatus = (appointmentId: string, nextPayments: PaymentRecord[]) => {
    setAppointments(prev =>
      prev.map(a => {
        if (a.id !== appointmentId) return a;
        // Preserve cancelled/no-show
        if (a.status === 'CANCELLED' || a.status === 'NO_SHOW') return a;
        const paid = computeIsPaid(appointmentId, nextPayments);
        if (paid && a.status !== 'COMPLETED') {
          return { ...a, status: 'COMPLETED' };
        }
        if (!paid && a.status === 'COMPLETED') {
          return { ...a, status: 'SCHEDULED' };
        }
        return a;
      })
    );
  };

  const handleRecordPayment = (payment: Omit<PaymentRecord, 'id' | 'createdAt'>) => {
    const newPayment: PaymentRecord = {
      ...payment,
      id: `pay-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setPayments(prev => {
      const next = [newPayment, ...prev];
      if (payment.appointmentId) {
        adjustAppointmentStatus(payment.appointmentId, next);
      }
      return next;
    });
    triggerToast('Payment recorded');
  };

  const handleUpdatePayment = (updated: PaymentRecord) => {
    setPayments(prev => {
      const next = prev.map(p => (p.id === updated.id ? updated : p));
      if (updated.appointmentId) {
        adjustAppointmentStatus(updated.appointmentId, next);
      }
      return next;
    });
    triggerToast('Payment updated');
  };

  const handleDeletePayment = (id: string) => {
    setPayments(prev => {
      const target = prev.find(p => p.id === id);
      const next = prev.filter(p => p.id !== id);
      if (target?.appointmentId) {
        adjustAppointmentStatus(target.appointmentId, next);
      }
      return next;
    });
    triggerToast('Payment deleted');
  };

  const handleRecordExpense = (expense: Omit<ExpenseRecord, 'id' | 'createdAt'>) => {
    const newExpense: ExpenseRecord = {
      ...expense,
      id: `exp-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setExpenses(prev => [newExpense, ...prev]);
    triggerToast('Expense recorded');
  };

  const handleDeleteExpense = (id: string) => {
    setExpenses(prev => prev.filter(e => e.id !== id));
    triggerToast('Expense deleted');
  };

  const handleLogin = () => {
    if (loginPin !== '3566') {
      alert('Invalid PIN');
      return;
    }
    if (loginRole === 'doctor' && !loginDoctorId) return;
    if (loginRole === 'patient' && !loginPatientId) return;
    if (loginRole === 'doctor') {
      setCurrentUser({ role: 'doctor', doctorId: loginDoctorId });
    } else if (loginRole === 'patient') {
      setCurrentUser({ role: 'patient', patientId: loginPatientId });
      setSelectedPatientId(loginPatientId);
    } else {
      setCurrentUser({ role: loginRole });
    }
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveTab('dashboard');
  };

  const handleCreateAppointment = (date: string, time: string) => {
    const providerId = currentUser?.role === 'doctor' ? currentUser.doctorId : dentists[0]?.id || 'd1';
    const room = rooms[0];
    const duration = 30;
    const patientId = currentUser?.role === 'patient' ? currentUser.patientId : '';

    const slots = findAvailableSlots(
      date,
      providerId,
      room,
      duration,
      appointments,
      8,
      18,
      clinicSettings.defaultSlotDuration || 30
    );

    const requested = time || '09:00';
    const requestedAppt: Omit<Appointment, 'id' | 'startTime'> = {
      ...getEmptyAppt(),
      providerId,
      room,
      duration: clinicSettings.defaultSlotDuration || duration,
      date,
      time: requested,
    };
    const conflict = checkAppointmentConflict(requestedAppt, appointments);
    const pickedTime = conflict.hasConflict && slots.length ? slots[0].time : requested;

    setEditingAppt(null);
    setApptForm({
      ...getEmptyAppt(),
      patientId,
      status: currentUser?.role === 'patient' ? 'REQUESTED' : 'SCHEDULED',
      providerId,
      room,
      duration: clinicSettings.defaultSlotDuration || duration,
      date,
      time: pickedTime,
    });
    setIsApptModalOpen(true);
  };

  const handleDeletePatient = (patientId: string) => {
    if (!window.confirm('Are you sure you want to delete this patient? This will also remove all their appointments.')) {
      return;
    }
    setPatients(prev => prev.filter(p => p.id !== patientId));
    setAppointments(prev => prev.filter(a => a.patientId !== patientId));
    if (selectedPatientId === patientId) {
      setSelectedPatientId(patients.find(p => p.id !== patientId)?.id ?? null);
    }
    triggerToast('Patient and records deleted');
  };

  const handleSaveSettings = (data: {
    settings: ClinicSettings;
    dentists: Dentist[];
    procedures: Procedure[];
    rooms: string[];
  }) => {
    const mergedSettings = { ...data.settings, treatmentRooms: data.rooms };
    setClinicSettings(mergedSettings);
    setDentists(data.dentists);
    setProcedures(data.procedures);
    triggerToast('Settings updated');
  };

  if (!currentUser) {
    return (
      <div className="flex min-h-screen bg-white font-sans text-slate-900">
        {/* Left Side - Branding & Visuals */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-600 to-indigo-800 text-white p-12 flex-col justify-between overflow-hidden">
          {/* Ambient Background Shapes */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-500/30 rounded-full blur-3xl" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-500/30 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl">
                <span className="font-black text-2xl tracking-tighter">DC</span>
              </div>
              <span className="font-bold text-xl tracking-wide opacity-90">DentalCore</span>
            </div>

            <h1 className="text-5xl font-black leading-tight mb-6">
              Manage your clinic <br />
              <span className="text-blue-200">with confidence.</span>
            </h1>
            <p className="text-lg text-blue-100 max-w-md leading-relaxed">
              Experience the next generation of clinical management. Streamlined workflows for admins, doctors, and patients.
            </p>
          </div>

          <div className="relative z-10 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl border border-white/10">
                <Icons.ShieldCheck className="w-6 h-6 mb-2 text-blue-200" />
                <h3 className="font-bold">HIPAA Ready</h3>
                <p className="text-xs text-blue-100 opacity-70">Secure & Compliant</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl border border-white/10">
                <Icons.Zap className="w-6 h-6 mb-2 text-blue-200" />
                <h3 className="font-bold">Real-time</h3>
                <p className="text-xs text-blue-100 opacity-70">Instant updates</p>
              </div>
            </div>

            <div className="pt-8 border-t border-white/10">
              <p className="text-sm text-blue-200">
                Powered by <a href="https://www.code2craft.com" className="text-white font-bold hover:underline decoration-blue-300 underline-offset-4">Code2Craft</a>
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 lg:p-12 bg-gray-50/50">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center lg:text-left">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">Welcome back</h2>
              <p className="text-slate-500 mt-2">Please select your role to continue.</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Role</label>
                <div className="grid grid-cols-2 gap-3">
                  {roleOptions.map(role => {
                    const IconComp = Icons[role.icon];
                    const active = loginRole === role.value;
                    return (
                      <button
                        key={role.value}
                        onClick={() => setLoginRole(role.value)}
                        className={`group relative p-4 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-2 text-center outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${active
                          ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                      >
                        <div className={`p-2 rounded-xl transition-colors ${active ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-white'}`}>
                          <IconComp className="w-5 h-5" />
                        </div>
                        <span className="font-bold text-sm">{role.label}</span>
                        {active && (
                          <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-white shadow-sm" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">

                {loginRole === 'doctor' && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Select Doctor</label>
                    <div className="relative">
                      <select
                        className="w-full appearance-none border-2 border-slate-200 rounded-xl p-3 pr-10 bg-slate-50 font-medium text-slate-700 focus:border-blue-500 focus:bg-white focus:outline-none transition-all"
                        value={loginDoctorId}
                        onChange={(e) => setLoginDoctorId(e.target.value)}
                      >
                        <option value="" disabled>Choose a provider...</option>
                        {dentists.map(doc => (
                          <option key={doc.id} value={doc.id}>{doc.name}</option>
                        ))}
                      </select>
                      <Icons.ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {loginRole === 'patient' && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Select Patient</label>
                    <div className="relative">
                      <select
                        className="w-full appearance-none border-2 border-slate-200 rounded-xl p-3 pr-10 bg-slate-50 font-medium text-slate-700 focus:border-blue-500 focus:bg-white focus:outline-none transition-all"
                        value={loginPatientId}
                        onChange={(e) => setLoginPatientId(e.target.value)}
                      >
                        <option value="" disabled>Choose a patient...</option>
                        {patients.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      <Icons.ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-sm font-bold text-slate-700">Access PIN</label>
                    <span className="text-xs text-blue-600 font-medium">Locked?</span>
                  </div>
                  <input
                    type="password"
                    value={loginPin}
                    onChange={(e) => setLoginPin(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl p-3 bg-slate-50 font-medium text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none transition-all"
                    placeholder="Enter your 8-digit PIN code"
                  />
                  <p className="text-xs text-slate-400 pl-1">
                    Security PIN is required for all roles.
                  </p>
                </div>
              </div>

              <button
                onClick={handleLogin}
                disabled={(loginRole === 'doctor' && !loginDoctorId) || (loginRole === 'patient' && !loginPatientId)}
                className="w-full py-4 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 focus:ring-4 focus:ring-slate-200 active:transform active:scale-[0.98] transition-all shadow-xl shadow-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Access Workspace
              </button>

              <div className="block lg:hidden text-center">
                <p className="text-xs text-slate-400">
                  Powered by <a href="https://www.code2craft.com" className="text-blue-600 font-bold">Code2Craft</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-slate-900 lg:flex">
      <div
        className={`fixed inset-y-0 left-0 z-30 w-72 transform bg-white border-r border-slate-200 shadow-lg transition-transform duration-200 lg:static lg:shadow-none lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <Sidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          clinicName={clinicSettings.clinicName}
          logoUrl={clinicSettings.logoUrl}
          onLogout={handleLogout}
          currentUserRole={currentUser.role}
          onNavigate={() => setSidebarOpen(false)}
        />
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-slate-900/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="flex-1 flex flex-col overflow-hidden">
        <Header
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onAddPatient={() => {
            setEditingPatient(null);
            setPatientForm(getEmptyPatient());
            setIsPatientModalOpen(true);
          }}
          onBookAppointment={() => {
            setEditingAppt(null);
            setApptForm(getEmptyAppt());
            setIsApptModalOpen(true);
          }}
          showAddPatient={currentUser?.role !== 'patient'}
          onToggleMenu={() => setSidebarOpen(prev => !prev)}
        />

        <Toast toast={showToast} />

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
          {activeTab === 'dashboard' && (
            <Dashboard
              patients={patients}
              appointments={visibleAppointments}
              userProfile={currentUserProfile}
            />
          )}
          {activeTab === 'patients' && (
            <PatientRegistry
              patients={filteredPatients}
              appointments={visibleAppointments}
              selectedPatientId={selectedPatientId}
              onSelectPatient={setSelectedPatientId}
              onEditPatient={handleEditPatient}
              panoramicAnalyses={PANORAMIC_ANALYSIS}
              onDeletePatient={currentUser?.role === 'admin' ? handleDeletePatient : undefined}
            />
          )}
          {activeTab === 'appointments' && (
            <AppointmentsView
              appointments={visibleAppointments}
              patients={patients}
              dentists={dentists}
              onEditAppointment={handleEditAppointment}
              onViewAppointment={handleEditAppointment}
              onDeleteAppointment={currentUser.role === 'patient' ? undefined : handleDeleteAppointment}
              onCollectPayment={currentUser.role === 'patient' ? undefined : handleCollectPaymentFromAppt}
              isPatient={currentUser.role === 'patient'}
            />
          )}
          {activeTab === 'followup' && (
            <FollowUpView
              patients={visiblePatients}
              appointments={visibleAppointments}
              dentists={dentists}
              onOpenPatient={
                currentUser.role === 'patient'
                  ? undefined
                  : (id) => {
                    setSearchQuery('');
                    setSelectedPatientId(id);
                    setActiveTab('patients');
                  }
              }
              onSendReminder={
                currentUser.role === 'patient'
                  ? undefined
                  : (id) => {
                    enqueueReminder(id);
                    setActiveTab('reminders');
                  }
              }
            />
          )}
          {activeTab === 'reminders' && (
            <ReminderQueue
              reminders={currentUser.role === 'patient' ? reminders.filter(r => r.patientId === currentUser.patientId) : reminders}
              onUpdateStatus={currentUser.role === 'patient' ? undefined : updateReminderStatus}
              onDelete={currentUser.role === 'patient' ? undefined : deleteReminder}
              isPatient={currentUser.role === 'patient'}
            />
          )}
          {activeTab === 'calendar' && (
            <CalendarView
              appointments={visibleAppointments}
              patients={patients}
              dentists={dentists}
              procedures={procedures}
              initialDate={calendarFocusDate || todayDateStr}
              onEditAppointment={handleEditAppointment}
              onCreateAppointment={handleCreateAppointment}
            />
          )}
          {activeTab === 'reports' && (
            <ReportsView
              appointments={visibleAppointments}
              patients={patients}
              dentists={dentists}
              currentUserRole={currentUser.role}
              payments={payments}
              expenses={expenses}
              onCollectPayment={currentUser.role === 'patient' ? undefined : () => setActiveTab('payments')}
            />
          )}
          {activeTab === 'panoramic' && (
            <PanoramicAnalysis />
          )}
          {activeTab === 'payments' && (
            <PaymentsView
              appointments={visibleAppointments}
              patients={currentUser.role === 'patient' ? visiblePatients : patients}
              dentists={dentists}
              clinicSettings={clinicSettings}
              payments={payments}
              intent={paymentIntent || undefined}
              onConsumeIntent={() => setPaymentIntent(null)}
              onRecordPayment={handleRecordPayment}
              onUpdatePayment={handleUpdatePayment}
              onDeletePayment={handleDeletePayment}
            />
          )}
          {activeTab === 'expenses' && (
            <ExpensesView
              expenses={expenses}
              onRecordExpense={handleRecordExpense}
              onDeleteExpense={handleDeleteExpense}
            />
          )}
          {activeTab === 'settings' && (
            (currentUser.role === 'admin' || currentUser.role === 'secretary') ? (
              <Settings
                dentists={dentists}
                procedures={procedures}
                clinicSettings={clinicSettings}
                onSave={handleSaveSettings}
              />
            ) : (
              <div className="bg-white rounded-3xl border shadow-sm p-8 text-slate-600">
                <h2 className="text-2xl font-black mb-3">Settings</h2>
                <p className="text-sm">Settings are managed by Admin/Secretary.</p>
              </div>
            )
          )}
        </div>
      </main>

      <PatientModal
        isOpen={isPatientModalOpen}
        editingPatient={editingPatient}
        patientForm={patientForm}
        onClose={() => setIsPatientModalOpen(false)}
        onSave={handleSavePatient}
        onFormChange={setPatientForm}
        contactOnly={currentUser?.role === 'patient'}
      />

      <AppointmentModal
        isOpen={isApptModalOpen}
        editingAppt={editingAppt}
        apptForm={apptForm}
        patients={patients}
        dentists={dentists}
        procedures={procedures}
        rooms={rooms}
        appointments={visibleAppointments}
        lockedPatientId={currentUser?.role === 'patient' ? currentUser.patientId : undefined}
        allowStatusEdit={currentUser?.role !== 'patient'}
        isPatient={currentUser?.role === 'patient'}
        onClose={() => setIsApptModalOpen(false)}
        onSave={handleSaveAppt}
        onFormChange={setApptForm}
      />
    </div>
  );
};

export default App;
